import { useMemo } from 'react';
import { usePrivateResearchItemDetails } from './usePrivateResearchItemDetails';
import type { PrivateResearchItem } from './types';

interface UsePrivateResearchDataTransformProps {
  allResearch: PrivateResearchItem[];
  selectedResearch: PrivateResearchItem | null;
}

export const usePrivateResearchDataTransform = ({
  allResearch,
  selectedResearch,
}: UsePrivateResearchDataTransformProps) => {
  // Use dedicated item details hook for selected research (real-time updates)
  const {
    data: selectedResearchDetails,
    isLoading: isLoadingDetails,
    error: detailsError
  } = usePrivateResearchItemDetails(selectedResearch?.id || null);

  // Transform private research to match ResearchLibraryItem interface
  const transformedResearch = useMemo(() => {
    return allResearch.map(item => ({
      ...item,
      favorite_count: 0, // Private research doesn't have favorites
      is_favorited: false, // Private research doesn't have favorites
      research_type: typeof item.research_type === 'string' ? item.research_type : 'General Research'
    }));
  }, [allResearch]);

  // Transform selected research to match ResearchItem interface
  // Use detailed data from dedicated hook when available for real-time updates
  const transformedSelectedResearch = useMemo(() => {
    if (!selectedResearch) return null;
    
    // Use detailed data if available (includes real-time related_questions updates)
    const sourceData = selectedResearchDetails || selectedResearch;
    
    return {
      ...sourceData,
      researchType: typeof sourceData.research_type === 'string' ? sourceData.research_type : 'General Research',
      createdAt: sourceData.created_at
    };
  }, [selectedResearch, selectedResearchDetails]);

  return {
    transformedResearch,
    transformedSelectedResearch,
    selectedResearchDetails,
    isLoadingDetails,
    detailsError,
  };
}; 